import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Loader2, X, Mail, Phone, MapPin, FileText, History, Trash2, KeyRound, Send, GitMerge,
  AlertTriangle, Save, ChevronRight, Repeat, Pause, Play, SkipForward, CreditCard,
} from "lucide-react";
import { api } from "../lib/config";
import { toast } from "sonner";
import RecurringFrequencyFields from "./RecurringFrequencyFields";

const PHONE_RE = /^\+?[\d\s().-]{7,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EditCustomerDialog({ open, onClose, customer, customers = [], services = [], onSaved, onOpenCustomer }) {
  const [tab, setTab] = useState("edit"); // edit | recurring | history | danger
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    city: "", state: "", zip_code: "", notes: "",
  });
  // Conflict state when another ACTIVE customer already owns the email/phone.
  // Shape: { field: "email"|"phone", message: string, existing_customer: {...} }
  const [conflict, setConflict] = useState(null);
  const [rec, setRec] = useState({
    service_frequency: "one_time", custom_days: null,
    preferred_service_day: "any", preferred_time_window: "morning",
    start_date: "", service_key: "lawn_mowing", square_feet: 2000, yard_area: "both",
  });
  const [recStatus, setRecStatus] = useState("active");
  const [recCounts, setRecCounts] = useState({ next: null, last: null, generated: 0 });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;
    setForm({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.default_address || "",
      city: customer.city || "",
      state: customer.state || "",
      zip_code: customer.zip_code || "",
      notes: customer.notes || "",
      require_prepayment: !!customer.require_prepayment,  // iter185
    });
    const sched = customer.recurring_schedule || {};
    setRec({
      service_frequency: sched.frequency || "one_time",
      custom_days: sched.custom_days ?? null,
      preferred_service_day: sched.preferred_service_day || "any",
      preferred_time_window: sched.preferred_time_window || "morning",
      start_date: sched.start_date || "",
      service_key: sched.service_key || "lawn_mowing",
      square_feet: sched.square_feet ?? 2000,
      yard_area: sched.yard_area || "both",
    });
    setRecStatus(sched.status || "active");
    setRecCounts({ next: sched.next_service_date, last: sched.last_service_date, generated: 0 });
    setErrors({});
    setConflict(null);
    setTab("edit");
  }, [open, customer]);

  const loadHistory = async () => {
    if (!customer?.user_id) return;
    try {
      const { data } = await api.get(`/admin/customers/${customer.user_id}/history`);
      setHistory(data.history || []);
    } catch { /* noop */ }
  };
  useEffect(() => { if (tab === "history") loadHistory(); /* eslint-disable-next-line */ }, [tab, customer?.user_id]);

  const validate = () => {
    const e = {};
    if (form.email && !EMAIL_RE.test(form.email)) e.email = "Enter a valid email";
    if (form.phone && !PHONE_RE.test(form.phone)) e.phone = "Enter a valid phone";
    if (form.zip_code && !/^\d{5}(-\d{4})?$/.test(form.zip_code)) e.zip_code = "5-digit ZIP";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setConflict(null);
    setBusy("save");
    try {
      const { data } = await api.patch(`/admin/customers/${customer.user_id}`, form);
      const n = data.history_entries?.length || 0;
      toast.success(n ? `Saved · ${n} change${n === 1 ? "" : "s"}` : "Saved");
      onSaved?.(data.customer);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Structured 409 for email/phone already owned by another ACTIVE customer.
      if (
        e?.response?.status === 409 &&
        detail && typeof detail === "object" &&
        (detail.code === "email_in_use" || detail.code === "phone_in_use") &&
        detail.existing_customer
      ) {
        setConflict({
          field: detail.field || (detail.code === "email_in_use" ? "email" : "phone"),
          message: detail.message || "This value belongs to another customer.",
          existing_customer: detail.existing_customer,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not save"));
      }
    } finally { setBusy(null); }
  };

  const openExistingCustomer = () => {
    const target = conflict?.existing_customer;
    if (!target) return;
    setConflict(null);
    if (onOpenCustomer) {
      onOpenCustomer(target);
    } else {
      // Fallback: close so admin can find via search
      toast.info("Open the existing customer from the list to continue.");
      onClose?.();
    }
  };

  const saveRecurring = async () => {
    setBusy("rec-save");
    try {
      const { data } = await api.put(`/admin/customers/${customer.user_id}/recurring`, {
        service_frequency: rec.service_frequency,
        custom_days: rec.custom_days,
        preferred_service_day: rec.preferred_service_day,
        preferred_time_window: rec.preferred_time_window,
        start_date: rec.start_date || null,
        service_key: rec.service_key,
        square_feet: rec.square_feet,
        yard_area: rec.yard_area,
      });
      toast.success(`Schedule saved · ${data.recurring_generated || 0} visits regenerated`);
      onSaved?.(data.customer);
      // Close the dialog so the customer list can refresh with fresh data.
      // Reopening the dialog then reseeds `rec` from the updated customer,
      // preventing stale-preference bugs (Mon → Thu appearing to stay on Mon).
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule");
    } finally { setBusy(null); }
  };

  const recurringAction = async (action) => {
    setBusy(`rec-${action}`);
    try {
      const { data } = await api.post(`/admin/customers/${customer.user_id}/recurring/${action}`);
      const labels = { pause: "paused", resume: "resumed", "skip-next": "skipped next visit", cancel: "cancelled" };
      toast.success(`Service ${labels[action] || action}`);
      if (action === "pause") setRecStatus("paused");
      else if (action === "resume") setRecStatus("active");
      else if (action === "cancel") setRecStatus("cancelled");
      if (data?.recurring_generated != null) {
        setRecCounts((c) => ({ ...c, generated: data.recurring_generated }));
      }
      onSaved?.(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action}`);
    } finally { setBusy(null); }
  };

  const softDelete = async () => {
    if (!window.confirm(`Delete ${customer.name || customer.email}? They'll be hidden from the CRM but their history is preserved.`)) return;
    setBusy("delete");
    try {
      await api.delete(`/admin/customers/${customer.user_id}`);
      toast.success("Customer deleted");
      onSaved?.(null);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally { setBusy(null); }
  };

  const resetPw = async () => {
    if (!window.confirm("Generate a temporary password and SMS+email it to the customer?")) return;
    setBusy("reset");
    try {
      const { data } = await api.post(`/admin/customers/${customer.user_id}/reset-password`);
      toast.success(`Temp password sent · ${data.temp_password}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset");
    } finally { setBusy(null); }
  };

  const sendLogin = async () => {
    setBusy("login");
    try {
      await api.post(`/admin/customers/${customer.user_id}/send-login-link`);
      toast.success("Sign-in link sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send link");
    } finally { setBusy(null); }
  };

  // iter185 — Toggle per-customer "Require Prepayment" override
  const togglePrepay = async () => {
    const current = !!form.require_prepayment;
    const next = !current;
    setBusy("prepay");
    try {
      await api.put(`/admin/customers/${customer.user_id}/require-prepayment`,
                    { require_prepayment: next, reason: null });
      setForm({ ...form, require_prepayment: next });
      toast.success(next
        ? "Prepayment required for this customer's future bookings"
        : "Prepayment override removed — future bookings will use AutoPay on completion");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusy(null); }
  };

  const mergeInto = async (keepId) => {
    if (!window.confirm(`Merge this customer INTO the selected one? All bookings/quotes/payments will move and this account will be soft-deleted. This cannot be undone.`)) return;
    setBusy("merge");
    try {
      await api.post(`/admin/customers/merge`, { keep_user_id: keepId, merge_user_id: customer.user_id });
      toast.success("Customers merged");
      onSaved?.(null);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not merge");
    } finally { setBusy(null); setMergeOpen(false); }
  };

  if (!open || !customer) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 z-[80] bg-black/55 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="edit-customer-dialog">
      <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-br from-[#062818] to-[#0B4029] text-white px-5 py-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white text-[#047857] flex items-center justify-center font-extrabold text-lg shrink-0">
            {(customer.name || customer.email || "?")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-200">Edit customer</div>
            <div className="text-lg font-extrabold tracking-tight font-[Outfit] truncate">{customer.name || customer.email}</div>
            <div className="text-[11px] text-emerald-100/80 truncate">#{customer.user_id?.slice(-8)}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 grid grid-cols-4 gap-1 bg-gray-50 border-b border-gray-200">
          {[
            { k: "edit", label: "Details", icon: FileText },
            { k: "recurring", label: "Recurring", icon: Repeat },
            { k: "history", label: "History", icon: History },
            { k: "danger", label: "Actions", icon: AlertTriangle },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`edit-customer-tab-${k}`}
              className={`flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-extrabold uppercase tracking-[0.16em] rounded-t-xl transition ${tab === k ? "bg-white text-[#047857] border-x border-t border-gray-200" : "text-gray-500 hover:text-gray-800"}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto p-5 flex-1">
          {tab === "edit" && (
            <div className="space-y-4">
              <Field label="Customer name" testid="edit-cust-name">
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" data-testid="edit-cust-name-input" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" testid="edit-cust-email" error={errors.email}>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com" data-testid="edit-cust-email-input" />
                </Field>
                <Field label="Phone" testid="edit-cust-phone" error={errors.phone}>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(904) 555-0100" data-testid="edit-cust-phone-input" />
                </Field>
              </div>
              <Field label="Street address" testid="edit-cust-address">
                <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St" data-testid="edit-cust-address-input" />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="City" testid="edit-cust-city">
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="edit-cust-city-input" />
                </Field>
                <Field label="State" testid="edit-cust-state">
                  <Input value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase().slice(0, 2))} maxLength={2} data-testid="edit-cust-state-input" />
                </Field>
                <Field label="ZIP" testid="edit-cust-zip" error={errors.zip_code}>
                  <Input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} maxLength={10} data-testid="edit-cust-zip-input" />
                </Field>
              </div>
              <Field label="Notes" testid="edit-cust-notes">
                <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Gate code, dog at back, billing preference, etc." data-testid="edit-cust-notes-input" />
              </Field>

              {conflict && (
                <div
                  className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4"
                  data-testid="edit-customer-conflict"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-rose-700 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-rose-900">
                        {conflict.field === "phone" ? "Phone already in use" : "Email already in use"}
                      </div>
                      <div className="text-sm font-extrabold text-gray-900 mt-0.5">
                        {conflict.message}
                      </div>
                      <div className="mt-2 rounded-xl bg-white border border-rose-200 px-3 py-2">
                        <div
                          className="text-sm font-extrabold text-gray-900 truncate"
                          data-testid="conflict-existing-name"
                        >
                          {conflict.existing_customer.name || conflict.existing_customer.email || conflict.existing_customer.user_id}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {conflict.existing_customer.email}
                          {conflict.existing_customer.phone ? ` · ${conflict.existing_customer.phone}` : ""}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          onClick={openExistingCustomer}
                          className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold"
                          data-testid="conflict-open-existing"
                        >
                          Open Existing Customer
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setConflict(null)}
                          className="rounded-full"
                          data-testid="conflict-cancel"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "recurring" && (
            <div className="space-y-4" data-testid="edit-cust-recurring">
              <div className="rounded-2xl bg-gray-50 border border-gray-200 px-3 py-3">
                <div className="flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-[#047857]" />
                  <div className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-700">Current schedule</div>
                  <span className={`ml-auto text-[10px] uppercase font-extrabold tracking-wider px-2 py-0.5 rounded-full border ${
                    recStatus === "active" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                    recStatus === "paused" ? "bg-amber-100 text-amber-800 border-amber-200" :
                    "bg-rose-100 text-rose-800 border-rose-200"
                  }`} data-testid="rec-status-pill">{recStatus}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">Next service</div>
                    <div className="font-bold" data-testid="rec-next-date">{recCounts.next || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">Last service</div>
                    <div className="font-bold" data-testid="rec-last-date">{recCounts.last || "—"}</div>
                  </div>
                </div>
              </div>

              <RecurringFrequencyFields value={rec} onChange={setRec} services={services} />

              <div className="grid grid-cols-2 gap-2">
                <Button onClick={saveRecurring} disabled={busy} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold" data-testid="rec-save-btn">
                  {busy === "rec-save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save & regenerate</>}
                </Button>
                <Button onClick={() => recurringAction("skip-next")} disabled={busy} variant="outline" className="rounded-full" data-testid="rec-skip-btn">
                  {busy === "rec-skip-next" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><SkipForward className="w-4 h-4 mr-1.5" />Skip next visit</>}
                </Button>
                {recStatus === "active" ? (
                  <Button onClick={() => recurringAction("pause")} disabled={busy} variant="outline" className="rounded-full" data-testid="rec-pause-btn">
                    {busy === "rec-pause" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Pause className="w-4 h-4 mr-1.5" />Pause service</>}
                  </Button>
                ) : (
                  <Button onClick={() => recurringAction("resume")} disabled={busy || recStatus === "cancelled"} variant="outline" className="rounded-full" data-testid="rec-resume-btn">
                    {busy === "rec-resume" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1.5" />Resume service</>}
                  </Button>
                )}
                <Button onClick={() => { if (window.confirm("Cancel recurring service? Future generated visits will be removed.")) recurringAction("cancel"); }} disabled={busy} variant="outline" className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="rec-cancel-btn">
                  <X className="w-4 h-4 mr-1.5" />Cancel recurring
                </Button>
              </div>
            </div>
          )}

          {tab === "history" && (
            <div className="space-y-2" data-testid="edit-cust-history">
              {history.length === 0 ? (
                <div className="text-center text-gray-500 py-10">No edits yet.</div>
              ) : history.map((h, i) => (
                <div key={i} className="rounded-xl border border-gray-200 px-3 py-2 text-[12px]">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">
                    <span>{h.field}</span>
                    <span>{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                  </div>
                  <div className="mt-0.5 text-gray-900">
                    <span className="line-through text-rose-600">{String(h.old ?? "—")}</span>
                    <ChevronRight className="w-3 h-3 inline mx-1.5 text-gray-400" />
                    <span className="font-extrabold text-emerald-700">{String(h.new ?? "—")}</span>
                  </div>
                  {h.by && <div className="text-[10px] text-gray-500 mt-0.5">by {h.by}</div>}
                </div>
              ))}
            </div>
          )}

          {tab === "danger" && (
            <div className="space-y-3" data-testid="edit-cust-actions">
              <ActionRow icon={Send}     onClick={sendLogin} busy={busy === "login"} testid="edit-cust-send-login" title="Send login link" desc="Email + SMS a 24-hour magic sign-in link." />
              <ActionRow icon={KeyRound} onClick={resetPw}   busy={busy === "reset"} testid="edit-cust-reset-pw" title="Reset password" desc="Generate a temp password & message it to the customer." />
              <ActionRow
                icon={CreditCard}
                onClick={togglePrepay}
                busy={busy === "prepay"}
                testid="edit-cust-require-prepayment"
                title={form.require_prepayment ? "Prepayment: ON (turn off)" : "Require prepayment"}
                desc={form.require_prepayment
                  ? "Every future booking will be charged upfront via Stripe Checkout."
                  : "Charge this customer upfront (mode=payment) instead of the default AutoPay-on-completion."}
                tone={form.require_prepayment ? "amber" : undefined}
              />
              <ActionRow icon={GitMerge} onClick={() => setMergeOpen(true)} busy={busy === "merge"} testid="edit-cust-merge" title="Merge into another customer" desc="Move all bookings, quotes, and payments to a duplicate record." tone="amber" />
              <ActionRow icon={Trash2}   onClick={softDelete} busy={busy === "delete"} testid="edit-cust-delete" title="Delete customer" desc="Soft-delete: hides from CRM, history preserved." tone="rose" />

              {mergeOpen && (
                <div className="mt-2 rounded-2xl border-2 border-amber-300 bg-amber-50 p-3" data-testid="merge-picker">
                  <div className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-amber-900 mb-2">Pick the keeper</div>
                  <div className="max-h-48 overflow-y-auto space-y-1.5">
                    {customers.filter((c) => c.user_id !== customer.user_id && !c.is_admin && !c.merged_into).slice(0, 50).map((c) => (
                      <button
                        key={c.user_id}
                        type="button"
                        onClick={() => mergeInto(c.user_id)}
                        data-testid={`merge-target-${c.user_id}`}
                        className="w-full text-left bg-white rounded-xl border border-amber-200 px-3 py-2 hover:border-amber-400"
                      >
                        <div className="text-sm font-extrabold text-gray-900 truncate">{c.name || c.email || c.user_id}</div>
                        <div className="text-[11px] text-gray-500 truncate">{c.email} {c.phone && `· ${c.phone}`}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {tab === "edit" && (
          <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-end gap-2 bg-gray-50">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy === "save"} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold" data-testid="edit-customer-save">
              {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, children, error }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {error && <div className="mt-1 text-[11px] font-bold text-rose-600">{error}</div>}
    </div>
  );
}

function ActionRow({ icon: Icon, title, desc, onClick, busy, testid, tone = "default" }) {
  const tones = {
    default: "border-gray-200 hover:border-emerald-300 text-gray-900",
    amber:   "border-amber-300 text-amber-900 hover:bg-amber-50",
    rose:    "border-rose-300 text-rose-900 hover:bg-rose-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`w-full text-left bg-white rounded-2xl border-2 px-4 py-3 flex items-start gap-3 transition disabled:opacity-60 ${tones[tone]}`}
    >
      <Icon className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold tracking-tight">{title}</div>
        <div className="text-[12px] text-gray-600">{desc}</div>
      </div>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
    </button>
  );
}
